import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { useRemoveMerchandisingCollection } from '@/hooks/useRemoveMerchandisingCollection';
import { MerchandisingCollection } from '@/services/MerchandisingCollectionsService';

interface ConfirmRemoveCollectionModalProps {
  collectionToRemove: MerchandisingCollection | null;
  onRemove: () => void;
}

export function ConfirmRemoveCollectionModal({
  collectionToRemove,
  onRemove,
}: ConfirmRemoveCollectionModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.components.ConfirmRemoveCollectionModal',
  });

  const { removeMerchandisingCollection, isLoading } =
    useRemoveMerchandisingCollection();

  async function handleRemoveCollection() {
    if (!collectionToRemove) {
      return;
    }

    try {
      await removeMerchandisingCollection(collectionToRemove.id);
      onRemove();
      toast.success(t('toast.success'));
    } catch {
      toast.error(t('toast.error'));
    }
  }

  return (
    <AlertDialog open={!!collectionToRemove} onOpenChange={onRemove}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('description', { name: collectionToRemove?.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel type="button">
            {t('cancelButtonLabel')}
          </AlertDialogCancel>

          <AlertDialogAction
            type="button"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleRemoveCollection}
          >
            {isLoading ? t('removeButtonLabelLoading') : t('removeButtonLabel')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
